// Utils
import { getDisplayName, getEmail, getFamilyName, getGivenName } from '../../../common/users/userHelper';
import { ImMNUser } from '../../../common/users/userModelTypes';
import { getShareCount } from '../../../common/users/utils/userPropertyUtils';
import { prop } from '../../../common/utils/immutableHelper';

type SortableUser = {
    name: {
        givenName: string;
        familyName: string;
        displayName: string;
    };
    email: string;

    disabled?: boolean;
    sharingCurrentBoard?: boolean;
    shareCount?: number;
};

type UserRankFunction = (searchQuery: string) => (user: SortableUser | ImMNUser) => number;

const getMatchScore = (field: string, query: string, value: number): number => {
    if (!field) return 0;

    const lowerField = field.toLowerCase();
    const match = lowerField.indexOf(query);

    if (match < 0) return 0;
    if (match === 0) return 3 * value;
    return value;
};

export const getUserNameMatchScore: UserRankFunction = (searchQuery) => {
    const query = searchQuery.toLowerCase();

    return (user) =>
        Math.max(
            getMatchScore(getGivenName(user), query, 5),
            getMatchScore(getFamilyName(user), query, 5),
            getMatchScore(getDisplayName(user), query, 2),

            // weak weighting on email -- will still outweigh names when user
            // is searching by email, as names don't have .'s and @'s in them
            getMatchScore(getEmail(user), query, 1),
        );
};

export const getMentionSortRanking: UserRankFunction = (searchQuery) => {
    const nameMatchScoreFn = getUserNameMatchScore(searchQuery);

    return (user) => {
        let score = nameMatchScoreFn(user);

        const userIsDisabled = prop('disabled', user);
        if (score > 0 && !userIsDisabled) {
            score += 20;
        }

        return score;
    };
};

export const getShareSortRanking: UserRankFunction = (searchQuery) => {
    const nameMatchScoreFn = getUserNameMatchScore(searchQuery);
    return (user) => {
        let score = nameMatchScoreFn(user);

        if (score === 0) return 0;

        const shareCount = getShareCount(user);
        score += shareCount * 100;

        return score;
    };
};
